package com.dotdrives.model

import kotlinx.datetime.LocalDate
import kotlin.time.Instant

/**
 * `donations` is insert-only (ADR 0003) and gated on `has_perm_strict` for both read and
 * write (docs/schema.md: "Donations (+ pledges, receipts, payment references)") — a `field`
 * member gets zero rows back from any query against this table, never a partial row. A
 * reversal (`reversesDonationId != null`) always carries a negative `amount`, enforced by
 * `donations_reversal_amount_sign_chk`; callers that sum a list of these must net, not sum
 * absolute values.
 */
data class Donation(
    val id: String,
    val orgId: String,
    val giverId: String,
    val giverDisplayName: String,
    val driveId: String?,
    val driveTitle: String?,
    val pledgeId: String?,
    val targetId: String?,
    val targetName: String?,
    val stewardUserId: String?,
    val stewardName: String?,
    val amount: Double,
    val feeAmount: Double,
    val coveredByGiver: Boolean,
    val paymentType: String,
    val source: String,
    val memo: String,
    val occurredAt: Instant,
    val recordedAt: Instant,
    val reversesDonationId: String?,
    val receiptSentAt: Instant?,
    val qboSyncedAt: Instant?,
    val qboSyncError: String?,
    /** Null for verified provider/system-origin gifts; manual gifts retain the staff actor. */
    val createdBy: String?,
    val qboWorkflowStatus: String? = null,
    /** False only for imported legacy history that must remain visible but non-actionable. */
    val isActive: Boolean = true,
) {
    val target: String? get() = targetName
    val isReversal: Boolean get() = reversesDonationId != null
}

/** New table (docs/schema.md): the pledge-chase automation rule's own state row. */
data class Pledge(
    val id: String,
    val orgId: String,
    val giverId: String,
    val giverDisplayName: String,
    val driveId: String?,
    val driveTitle: String?,
    val targetId: String?,
    val targetName: String?,
    val amount: Double,
    val expectedBy: LocalDate?,
    val fulfilledAt: Instant?,
    val fulfillingDonationId: String?,
    val occurredAt: Instant,
    val createdBy: String,
)

/** One row per giver per tax year (docs/schema.md: "the year-end receipt batch"). */
data class GivingStatement(
    val id: String,
    val orgId: String,
    val giverId: String,
    val giverDisplayName: String,
    val year: Int,
    val totalAmount: Double,
    val giftCount: Int,
    val pdfStoragePath: String?,
    val generatedAt: Instant?,
    val emailedAt: Instant?,
    val mailedAt: Instant?,
)

/** Aggregated preview row before a giving_statements row is ever written. */
data class GivingStatementDraft(
    val giverId: String,
    val giverDisplayName: String,
    val totalAmount: Double,
    val giftCount: Int,
)

data class GivingStatementBatchSummary(
    val year: Int,
    val drafts: List<GivingStatementDraft>,
    val alreadyGeneratedGiverIds: Set<String>,
    val statements: List<GivingStatement> = emptyList(),
) {
    val giverCount: Int get() = drafts.size
    val giftCount: Int get() = drafts.sumOf { it.giftCount }
    val totalAmount: Double get() = drafts.sumOf { it.totalAmount }
    val pendingCount: Int get() = drafts.count { it.giverId !in alreadyGeneratedGiverIds }
}

data class NewDonation(
    val giverId: String,
    val amount: Double,
    val driveId: String?,
    val pledgeId: String?,
    val targetId: String?,
    val paymentType: String,
    val source: String,
    val memo: String,
    val occurredAt: Instant,
    val coveredByGiver: Boolean = false,
    val feeAmount: Double = 0.0,
    /** Set only for a refund/correction row -- `donations_reversal_amount_sign_chk` requires
     * `amount < 0` whenever this is non-null (docs/schema.md). `record()` does not enforce
     * the sign itself; callers building a reversal (see `DonationDetailController.reverse`)
     * are responsible for negating the original amount. */
    val reversesDonationId: String? = null,
)

/**
 * Prefill for the two-field quick-record dialog (design: "Two fields. We prefill the rest
 * from what we know"), derived from this giver's most recent gift and any open drive they
 * have — never from a fabricated default. All fields nullable: a first-time giver with no
 * open drive gets an entirely empty set of defaults, which the dialog must handle plainly.
 */
data class DonationSmartDefaults(
    val targetId: String?,
    val targetName: String?,
    val paymentType: String?,
    val driveId: String?,
    val driveTitle: String?,
    val pledgeId: String?,
    val pledgeAmount: Double?,
)

/**
 * One needs-attention queue entry. Two real categories only — `donations.qbo_sync_error`
 * and a pledge unpaid past `expected_by` are both state the schema actually carries. The
 * design's third category (a possible duplicate giver from CSV import) has no backing
 * signal anywhere in docs/schema.md or the shipped migrations, so it is not fabricated here.
 */
enum class AttentionKind { QboSyncError, UnpaidPledge }

data class AttentionItem(
    val kind: AttentionKind,
    val donation: Donation? = null,
    val pledge: Pledge? = null,
) {
    val key: String
        get() = when (kind) {
            AttentionKind.QboSyncError -> "sync:${donation?.id}"
            AttentionKind.UnpaidPledge -> "pledge:${pledge?.id}"
        }
}
